import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";

import HomeAppBar from "./components/home/header/AppBar";
import { useContext, useEffect } from "react";
import { AppContext } from "./context/AppContext";
import { useAuth } from './context/AuthContext';
import ScrollToTop from "./components/Common/ScrollToTop";
import CasinoGamesPage from "./components/Pages/Casino/CasinoGamesPage";
import LiveCasino from "./components/Pages/Casino/LiveCasino";
import Roulette from "./components/Pages/Casino/Roulette";
import Slots from "./components/Pages/Casino/Slots";
import SportsGamesPage from "./components/Pages/Casino/SportsGamesPage";
import SportsMain from "./pages/Sports/SportsMain";
import SportsMatch from "./pages/Sports/SportsMatch";
import SportsbookHistoryPage from "./pages/SportsbookHistoryPage";
import "./pages/Sports/sports-theme.css";
import GameLaunchNavigator from "./components/Pages/GamePlayer/GameLaunchNavigator";
import GameMiniPlayer from "./components/Pages/GamePlayer/GameMiniPlayer";
import PersistentGameFrame from './features/gamePlayer/component/PersistentGameFrame.component';
import GamePlayerPage from "./components/Pages/GamePlayer/GamePlayerPage";
import AttendanceCheckPage from "./components/Pages/UserFeatures/AttendanceCheckPage";
import UserFeatureListPage from "./components/Pages/UserFeatures/UserFeatureListPage";
import ProtectedRoute from "./components/Router/ProtectedRoute";
import CashierModal from "./components/Modals/CashierModals/CashierModal";
import SitePopup from "./components/Modals/SitePopup/SitePopup";
import AccountPageLayout from "./components/home/Account/AccountPageLayout";
import Profile from "./components/home/Account/Profile/Profile";
import PageLayout from "./components/home/MainHome/PageLayout";
import {
  ContentLayout,
  SportsContentLayout,
} from "./components/home/MainHome/styles";
import NavBar from "./components/home/header/NavBar/NavBar";
import SideBar from "./components/home/header/SideBar";
import AppRuntimeProviders from "./sportsbook/AppRuntimeProviders";
import SportsbookRuntimeProviders from "./sportsbook/SportsbookRuntimeProviders";
import useAppDeploymentRefresh from "./_hooks/appDeploymentRefresh.hook";
import DynamicSiteMetadata from "./components/DynamicSiteMetadata/DynamicSiteMetadata";
import LiveChat from "./components/LiveChat/LiveChat";
import { captureAffiliateReferralCode } from "./utils/affiliateReferralCode";
import GameLaunchBonusRecoveryHost from "./components/CommonModal/GameLaunchBonusRecoveryHost";

const AppDeploymentRefreshBridge = () => {
  useAppDeploymentRefresh();
  return null;
};

const AffiliateReferralCaptureBridge = () => {
  const { search } = useLocation();

  useEffect(() => {
    captureAffiliateReferralCode(search);
  }, [search]);

  return null;
};

const routesSportsLayout = [
  { path: "/sports", component: SportsMain },
  { path: "/sports/match/:id", component: SportsMatch },
];

const routesContentLayout = [
  { path: "/", component: HomeAppBar },
  { path: "/casino-games", component: CasinoGamesPage, protected: true },
  { path: "/live-casino", component: LiveCasino, protected: true },
  { path: "/roulette", component: Roulette, protected: true },
  { path: "/slots", component: Slots, protected: true },
  { path: "/sports-games", component: SportsGamesPage, protected: true },
  { path: "/play", component: GamePlayerPage, protected: true },
  {
    path: "/event",
    component: UserFeatureListPage,
    props: { mode: "events" },
    protected: true,
  },
  {
    path: "/customer-service-center",
    component: UserFeatureListPage,
    props: { mode: "customer" },
    protected: true,
  },
  {
    path: "/notice-board",
    component: UserFeatureListPage,
    props: { mode: "notes" },
    protected: true,
  },
  { path: "/attendance-check", component: AttendanceCheckPage, protected: true },
  { path: "/betting-history", component: SportsbookHistoryPage, protected: true },
];

const routesAccountLayout = [
  { path: "/my-page", component: Profile },
  { path: "/account/profile", component: Profile },
];

function App() {
  const { user } = useAuth();
  const {
    isTabletScreen,
    isAttendanceModalOpen,
    updateAttendanceModal,
    cashierModal,
    closeCashierModal,
  } = useContext(AppContext);

  return (
    <BrowserRouter>
      <AppRuntimeProviders>
        <AppDeploymentRefreshBridge />
        <AffiliateReferralCaptureBridge />
        <DynamicSiteMetadata />
        <LiveChat />
        <GameLaunchNavigator />
        <GameLaunchBonusRecoveryHost />
        <ScrollToTop />
        <NavBar />
        <SideBar />

        <PageLayout>
          <Routes>
            {routesSportsLayout.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute>
                    <SportsContentLayout
                      className="sportsbook-theme crypto-sportsbook-host sportsbook-route-shell"
                      $isTabletScreen={isTabletScreen}
                    >
                      <SportsbookRuntimeProviders>
                        <route.component />
                      </SportsbookRuntimeProviders>
                    </SportsContentLayout>
                  </ProtectedRoute>
                }
              />
            ))}
            <Route path="/international" element={<Navigate to="/sports?type=INTERNATIONAL" replace />} />
            <Route path="/inplay" element={<Navigate to="/sports?type=LIVE" replace />} />
            <Route path="/domestic" element={<Navigate to="/sports?type=DOMESTIC" replace />} />
            <Route path="/sportsbook" element={<Navigate to="/sports?type=DOMESTIC" replace />} />
            <Route path="/my-bets" element={<Navigate to="/betting-history?tab=Sportsbook" replace />} />
            <Route path="/sports/my-bets" element={<Navigate to="/betting-history?tab=Sportsbook" replace />} />
          </Routes>

        <Routes>
          {routesContentLayout.map((route) => {
            const content = <route.component {...(route.props || {})} />;

            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ContentLayout
                    $isTabletScreen={isTabletScreen}
                    $isGamePlayerPage={route.path === "/play"}
                  >
                    {route.protected ? <ProtectedRoute>{content}</ProtectedRoute> : content}
                  </ContentLayout>
                }
              />
            );
          })}
        </Routes>

        <Routes>
          {routesAccountLayout.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ContentLayout $isTabletScreen={isTabletScreen}>
                  <ProtectedRoute>
                    <AccountPageLayout>
                      <route.component />
                    </AccountPageLayout>
                  </ProtectedRoute>
                </ContentLayout>
              }
            />
          ))}
        </Routes>
        </PageLayout>
        {isAttendanceModalOpen ? (
          <AttendanceCheckPage onRequestClose={() => updateAttendanceModal(false)} />
        ) : null}
        <CashierModal
          hideTrigger
          isOpen={cashierModal.isOpen}
          defaultView={cashierModal.view}
          onOpenChange={(nextIsOpen) => {
            if (!nextIsOpen) closeCashierModal();
          }}
        />
        <SitePopup />
        <GameMiniPlayer />
        <PersistentGameFrame accountKey={user?.isLoading ? undefined : user?.isAuthenticated ? String(user.id) : null} />
      </AppRuntimeProviders>
    </BrowserRouter>
  );
}

export default App;
